import {
  applyWatermarkContentType,
  applyWatermarkPosition,
  DEFAULT_WATERMARK_SETTINGS,
  WATERMARK_CONTENT_TYPES,
  WATERMARK_TILE_PRESETS,
  watermarkFontSizePx,
  watermarkLogoDisplaySize,
} from "../../../watermark/Popup/watermarkSettings";

/** Sign PDF input modes (Draw UI in modal; bake via image in M2). */
export const SIGN_PDF_MODES = {
  TYPE: "type",
  UPLOAD: "upload",
  DRAW: "draw",
};

/** Signature vs initials — two independent marks on the PDF. */
export const SIGN_PDF_MARK_KINDS = {
  SIGNATURE: "signature",
  INITIALS: "initials",
};

/**
 * Default on-document sizes (raster / CSS px on the page canvas).
 * Signature / Initials: font size (type) or mark height (upload / draw).
 * Date / Text: font size. Checkbox: box edge length.
 */
export const SIGN_PDF_DEFAULT_SIZE_PX = {
  [SIGN_PDF_MARK_KINDS.SIGNATURE]: 30,
  [SIGN_PDF_MARK_KINDS.INITIALS]: 28,
  date: 16,
  text: 16,
  checkbox: 30,
};

/** US Letter short side @ 72dpi — used to keep legacy `size` scale in sync. */
export const SIGN_PDF_SIZE_REF_BASIS = 612;

/** PDF raster scale for preview + bake (must match bakeWatermarkedPdf default). */
export const SIGN_PDF_RENDER_SCALE = 1.5;

/**
 * Typical fitted preview min edge — legacy placements without fontSizeNorm
 * were authored near this basis.
 */
export const SIGN_PDF_LEGACY_AUTHORING_BASIS = 480;

/** Min stage edge for norm ↔ px (matches watermark size basis). */
export function signPdfStageBasis(stageW, stageH) {
  const w = Math.max(120, Number(stageW) || 420);
  const h = Math.max(120, Number(stageH) || w);
  return Math.min(w, h);
}

export function signPdfLegacyNormFromPx(px) {
  const p = Math.max(1, Number(px) || 12);
  return p / SIGN_PDF_LEGACY_AUTHORING_BASIS;
}

export function resolveSignPdfFontSizeNorm(settings, stageW, stageH) {
  const norm = Number(settings?.fontSizeNorm);
  if (Number.isFinite(norm) && norm > 0) return norm;

  const px = Number(settings?.fontSizePx);
  if (Number.isFinite(px) && px > 0) {
    const refBasis = Number(settings?.fontSizeRefBasis);
    if (Number.isFinite(refBasis) && refBasis > 0) {
      return px / refBasis;
    }
    const size = Number(settings?.size);
    if (Number.isFinite(size) && size > 0) {
      const basis = signPdfStageBasis(stageW, stageH);
      const derived = watermarkFontSizePx(size, stageW, stageH);
      if (derived > 0) return derived / basis;
    }
    return signPdfLegacyNormFromPx(px);
  }

  const size = Number(settings?.size);
  if (Number.isFinite(size) && size > 0) {
    const basis = signPdfStageBasis(stageW, stageH);
    return watermarkFontSizePx(size, stageW, stageH) / basis;
  }

  return signPdfLegacyNormFromPx(defaultSignPdfSizePxForKind(settings?.kind || "text"));
}

export function signPdfSizeScaleFromPx(px, { logo = false } = {}) {
  const p = Math.max(1, Number(px) || 12);
  const factor = logo ? 0.07 : 0.024;
  return Math.max(
    0.5,
    Math.min(24, Math.round((p / (SIGN_PDF_SIZE_REF_BASIS * factor)) * 100) / 100)
  );
}

export function defaultSignPdfSizePxForKind(kind) {
  if (kind === SIGN_PDF_MARK_KINDS.SIGNATURE) {
    return SIGN_PDF_DEFAULT_SIZE_PX.signature;
  }
  if (kind === SIGN_PDF_MARK_KINDS.INITIALS) {
    return SIGN_PDF_DEFAULT_SIZE_PX.initials;
  }
  if (kind === "date") return SIGN_PDF_DEFAULT_SIZE_PX.date;
  if (kind === "checkbox") return SIGN_PDF_DEFAULT_SIZE_PX.checkbox;
  if (kind === "text") return SIGN_PDF_DEFAULT_SIZE_PX.text;
  return SIGN_PDF_DEFAULT_SIZE_PX.text;
}

/** Page-relative size → pixels for the current stage / bake canvas. */
export function resolveSignPdfFontSizePx(settings, stageW, stageH) {
  const basis = signPdfStageBasis(stageW, stageH);
  const norm = resolveSignPdfFontSizeNorm(settings, stageW, stageH);
  return Math.max(1, Math.round(norm * basis * 10) / 10);
}

/** Persist norm + legacy px/size fields when the user picks a pixel size. */
export function signPdfFontSizeFieldsFromPx(
  px,
  stageW,
  stageH,
  { logo = false } = {}
) {
  const basis = signPdfStageBasis(stageW, stageH);
  const fontSizePx = Math.max(1, Number(px) || 12);
  const fontSizeNorm = fontSizePx / basis;
  return {
    fontSizePx,
    fontSizeNorm,
    fontSizeRefBasis: basis,
    size: signPdfSizeScaleFromPx(fontSizePx, { logo }),
  };
}

/** Toolbar resize without stage — scale norm proportionally to px change. */
export function signPdfFontSizePatchForPlacement(
  placement,
  nextPx,
  { logo = false } = {}
) {
  const px = Math.max(1, Number(nextPx) || 12);
  const prevPx = Number(placement?.fontSizePx);
  const prevNorm = Number(placement?.fontSizeNorm);
  const fontSizeNorm =
    prevNorm > 0 && prevPx > 0
      ? prevNorm * (px / prevPx)
      : signPdfLegacyNormFromPx(px);
  return {
    fontSizePx: px,
    fontSizeNorm,
    size: signPdfSizeScaleFromPx(px, { logo }),
  };
}

export function withSignPdfFontSizeNorm(fields = {}, stageW = 0, stageH = 0) {
  if (Number(fields?.fontSizeNorm) > 0) return fields;
  const px = Number(fields?.fontSizePx);
  if (!(px > 0)) return fields;
  const basis = signPdfStageBasis(stageW, stageH);
  if (basis > 0 && stageW > 0 && stageH > 0) {
    return {
      ...fields,
      fontSizeNorm: px / basis,
      fontSizeRefBasis: basis,
    };
  }
  return {
    ...fields,
    fontSizeNorm: signPdfLegacyNormFromPx(px),
  };
}

/**
 * Measure text for Sign PDF selection / layout.
 * Unlike watermark measure, keeps full ascent+descent (no height cap) so the
 * selection box covers glyphs start→end and top→bottom (incl. descenders).
 */
export function measureSignPdfText(
  text,
  fontSizePx,
  fontFamily,
  fontWeight = "normal",
  { letterSpacing = 0, fontStyle = "normal" } = {}
) {
  const safeText = String(text || " ").trim() || " ";
  const baseSize = Math.max(1, Number(fontSizePx) || 12);
  const family = fontFamily || "Arial, Helvetica, sans-serif";
  const weight =
    fontWeight === "bold" || fontWeight === 700 || fontWeight === "700"
      ? "700"
      : "400";
  const style =
    fontStyle === "italic" || fontStyle === "oblique" ? "italic" : "normal";
  const tracking = Number(letterSpacing);
  const trackingEm = Number.isFinite(tracking) ? tracking / 100 : 0;

  let width = baseSize * safeText.length * 0.55;
  let ascent = baseSize * 0.8;
  let descent = baseSize * 0.25;
  let fontAscent = baseSize * 0.92;
  let fontDescent = baseSize * 0.28;

  if (typeof document !== "undefined") {
    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.font = `${style} ${weight} ${baseSize}px ${family}`;
        const m = ctx.measureText(safeText);
        width = Math.max(0, m.width);
        if (Number.isFinite(m.actualBoundingBoxAscent)) {
          ascent = m.actualBoundingBoxAscent;
        }
        if (Number.isFinite(m.actualBoundingBoxDescent)) {
          descent = m.actualBoundingBoxDescent;
        }
        if (Number.isFinite(m.fontBoundingBoxAscent)) {
          fontAscent = m.fontBoundingBoxAscent;
        }
        if (Number.isFinite(m.fontBoundingBoxDescent)) {
          fontDescent = m.fontBoundingBoxDescent;
        }
        // Italic / script side bearings can sit outside measureText width.
        if (Number.isFinite(m.actualBoundingBoxLeft)) {
          width += Math.max(0, -m.actualBoundingBoxLeft);
        }
        if (Number.isFinite(m.actualBoundingBoxRight)) {
          width += Math.max(0, m.actualBoundingBoxRight - m.width);
        }
      }
    } catch {
      // keep fallbacks
    }
  }

  if (trackingEm !== 0 && safeText.length > 1) {
    width += (safeText.length - 1) * trackingEm * baseSize;
  }

  const inkH = ascent + descent;
  const fontH = fontAscent + fontDescent;
  // Full vertical coverage — do not cap to ~1.05× fontSize (that clips text).
  let height = Math.max(inkH, fontH, baseSize * 1.15);

  const sidePad = baseSize * (style === "italic" ? 0.2 : 0.12);
  width = Math.max(1, width + sidePad * 2);
  height = Math.max(1, height + baseSize * 0.1);

  return { width, height };
}

/** Logo / checkbox mark box from absolute fontSizePx (height) or size scale. */
export function resolveSignPdfLogoDisplaySize(
  settings,
  stageW,
  stageH,
  naturalWidth = 0,
  naturalHeight = 0,
  cropRect = null
) {
  const resolvedPx = resolveSignPdfFontSizePx(settings, stageW, stageH);
  if (resolvedPx > 0) {
    const nw = Number(naturalWidth) || 0;
    const nh = Number(naturalHeight) || 0;
    let aspect = nw > 0 && nh > 0 ? nw / nh : 1;
    if (cropRect && typeof cropRect === "object") {
      const w = Math.max(0.05, Number(cropRect.w) || 1);
      const h = Math.max(0.05, Number(cropRect.h) || 1);
      aspect = (nw > 0 && nh > 0 ? nw / nh : 1) * (w / h);
    }
    const height = Math.max(4, resolvedPx);
    const width = Math.max(4, height * aspect);
    return { width, height };
  }
  return watermarkLogoDisplaySize(
    settings?.size,
    stageW,
    stageH,
    naturalWidth,
    naturalHeight,
    cropRect
  );
}

/** Page scope — All / This page / Per page (M4). */
export const SIGN_PDF_SCOPES = {
  ALL: "all",
  CURRENT: "current",
  PER_PAGE: "per_page",
};

/**
 * Job-oriented entry intents — map 1:1 to pageScope.
 * `pro` options may be paywalled at the chooser / Apply-to control.
 */
export const SIGN_PDF_INTENT_OPTIONS = [
  {
    id: SIGN_PDF_SCOPES.ALL,
    label: "Same on all pages",
    title: "Same signature everywhere",
    description:
      "Place your signature and fields once — apply the same layout to every page.",
    pro: false,
  },
  {
    id: SIGN_PDF_SCOPES.CURRENT,
    label: "This page only",
    title: "Sign one page",
    description:
      "Add your signature and fields on the current page only. Other pages stay unchanged.",
    pro: false,
  },
  {
    id: SIGN_PDF_SCOPES.PER_PAGE,
    label: "Different per page",
    title: "Custom fields per page",
    description:
      "Build a different signing layout on each page — ideal for multi-section docs.",
    pro: false,
  },
];

export const SIGN_PDF_SCOPE_OPTIONS = [
  {
    id: SIGN_PDF_SCOPES.ALL,
    label: "All pages",
    title: "Same signature and fields on every page",
    pro: false,
  },
  {
    id: SIGN_PDF_SCOPES.CURRENT,
    label: "One page",
    title: "Current page only",
    pro: false,
  },
  {
    id: SIGN_PDF_SCOPES.PER_PAGE,
    label: "Per page",
    title: "Different fields on different pages",
    pro: false,
  },
];

export function normalizeSignPdfPageScope(scope) {
  if (scope === SIGN_PDF_SCOPES.CURRENT) return SIGN_PDF_SCOPES.CURRENT;
  if (scope === SIGN_PDF_SCOPES.PER_PAGE) return SIGN_PDF_SCOPES.PER_PAGE;
  return SIGN_PDF_SCOPES.ALL;
}

export function hasSignPdfSigningIntent(session) {
  return Boolean(session?.signingIntentChosen);
}

export function setSignPdfSigningIntent(session, scope, pageNum = 1) {
  const nextScope = normalizeSignPdfPageScope(scope);
  let next = {
    ...session,
    signingIntentChosen: true,
    signPdfWhoChosen: true,
  };
  next = setSignPdfPageScope(next, nextScope, pageNum);
  return next;
}

export function setSignPdfWhoChosen(session, chosen = true) {
  return { ...session, signPdfWhoChosen: Boolean(chosen) };
}

/** Preview canvas looks for typed signatures (also drives mark color). */
export const SIGN_PDF_CANVAS_THEMES = [
  {
    id: "white_grey",
    label: "Black signature",
    textColor: "#111111",
  },
  {
    id: "black_grey",
    label: "White signature",
    textColor: "#FFFFFF",
  },
  {
    id: "black_blue",
    label: "Brand blue signature",
    textColor: "#4F46E5",
  },
];

/** White canvas + black ink — first switcher option and default. */
export const DEFAULT_SIGN_PDF_CANVAS_THEME_ID = "white_grey";

export function getSignPdfCanvasTheme(themeId) {
  return (
    SIGN_PDF_CANVAS_THEMES.find((t) => t.id === themeId) ||
    SIGN_PDF_CANVAS_THEMES[0]
  );
}

export const SIGN_PDF_MODE_OPTIONS = [
  {
    id: SIGN_PDF_MODES.TYPE,
    label: "Type",
    tip: "Type your name and pick a script font. Preview updates on the right.",
    available: true,
  },
  {
    id: SIGN_PDF_MODES.UPLOAD,
    label: "Upload",
    tip: "Upload a signature image onto the canvas. Select it to remove background, replace, or delete.",
    available: true,
  },
  {
    id: SIGN_PDF_MODES.DRAW,
    label: "Draw",
    tip: "Draw with your mouse or finger. Undo and clear are available on the pad.",
    available: true,
  },
];

/**
 * Map Sign PDF mode → watermark content type used by preview/bake.
 * Type → signature fonts/text; Upload/Draw → logo image path.
 */
export function signPdfModeToContentType(mode) {
  if (mode === SIGN_PDF_MODES.UPLOAD || mode === SIGN_PDF_MODES.DRAW) {
    return WATERMARK_CONTENT_TYPES.LOGO;
  }
  return WATERMARK_CONTENT_TYPES.SIGNATURE;
}

export function signPdfModeFromSettings(settings) {
  if (settings?.signPdfMode === SIGN_PDF_MODES.DRAW) {
    return SIGN_PDF_MODES.DRAW;
  }
  if (settings?.contentType === WATERMARK_CONTENT_TYPES.LOGO) {
    return SIGN_PDF_MODES.UPLOAD;
  }
  return SIGN_PDF_MODES.TYPE;
}

/** Force single-mark placement (no watermark tiling). Preserves offsets/color/size. */
export function withSignPdfSingleMark(settings) {
  const rawOp = Number(settings?.opacity);
  let opacity = 100;
  if (Number.isFinite(rawOp)) {
    // Recover accidental 0–1 fractions; otherwise clamp watermark 0–100 scale.
    if (rawOp > 0 && rawOp <= 1) opacity = Math.round(rawOp * 100);
    else opacity = Math.max(0, Math.min(100, rawOp));
  }
  return {
    ...settings,
    tile: "single",
    spacing: 0,
    enabled: true,
    opacity,
  };
}

/**
 * Switch Type ↔ Upload (Draw maps to image path when available).
 */
export function applySignPdfMode(settings, mode) {
  const nextType = signPdfModeToContentType(mode);
  const switched = applyWatermarkContentType(settings, nextType);
  return withSignPdfSingleMark({
    ...switched,
    signPdfMode: mode,
  });
}

function withCanvasTheme(settings, themeId = DEFAULT_SIGN_PDF_CANVAS_THEME_ID) {
  const theme = getSignPdfCanvasTheme(themeId);
  return {
    ...settings,
    signPdfCanvasTheme: theme.id,
    color: theme.textColor,
  };
}

/** Force default (black ink) canvas on a blank mark before opening the creator. */
export function withDefaultSignPdfCanvas(settings) {
  return withCanvasTheme(
    settings || createDefaultSignPdfSettings(),
    DEFAULT_SIGN_PDF_CANVAS_THEME_ID
  );
}

export function createDefaultSignPdfSettings() {
  const base = withSignPdfSingleMark(
    applyWatermarkPosition(
      {
        ...applyWatermarkContentType(
          {
            ...DEFAULT_WATERMARK_SETTINGS,
            ...WATERMARK_TILE_PRESETS.single,
          },
          WATERMARK_CONTENT_TYPES.SIGNATURE
        ),
      },
      "center"
    )
  );
  return {
    ...withCanvasTheme(base, DEFAULT_SIGN_PDF_CANVAS_THEME_ID),
    text: "",
    size: signPdfSizeScaleFromPx(SIGN_PDF_DEFAULT_SIZE_PX.signature),
    fontSizePx: SIGN_PDF_DEFAULT_SIZE_PX.signature,
    fontSizeNorm: signPdfLegacyNormFromPx(SIGN_PDF_DEFAULT_SIZE_PX.signature),
    opacity: 100,
    textAlign: "center",
    signPdfMode: SIGN_PDF_MODES.TYPE,
  };
}

/** Empty initials mark — smaller default size than signature. */
export function createDefaultSignPdfInitialsSettings() {
  const base = withSignPdfSingleMark(
    applyWatermarkPosition(
      {
        ...applyWatermarkContentType(
          {
            ...DEFAULT_WATERMARK_SETTINGS,
            ...WATERMARK_TILE_PRESETS.single,
            text: "",
          },
          WATERMARK_CONTENT_TYPES.SIGNATURE
        ),
      },
      "center"
    )
  );
  return {
    ...withCanvasTheme(base, DEFAULT_SIGN_PDF_CANVAS_THEME_ID),
    size: signPdfSizeScaleFromPx(SIGN_PDF_DEFAULT_SIZE_PX.initials),
    fontSizePx: SIGN_PDF_DEFAULT_SIZE_PX.initials,
    fontSizeNorm: signPdfLegacyNormFromPx(SIGN_PDF_DEFAULT_SIZE_PX.initials),
    text: "",
    opacity: 100,
    textAlign: "center",
    signPdfMode: SIGN_PDF_MODES.TYPE,
  };
}

export const DEFAULT_SIGN_PDF_SETTINGS = createDefaultSignPdfSettings();

export function createDefaultSignPdfSession() {
  return {
    signature: createDefaultSignPdfSettings(),
    initials: createDefaultSignPdfInitialsSettings(),
    activeMark: SIGN_PDF_MARK_KINDS.SIGNATURE,
    pageScope: SIGN_PDF_SCOPES.ALL,
    /** False until user picks All / One page / Per page on entry. */
    signingIntentChosen: false,
    /** True after user chooses Sign myself on the who modal. */
    signPdfWhoChosen: false,
    /** Per-page overrides (legacy / scope seed). Prefer placements[]. */
    byPage: {},
    /**
     * Saved mark library (legacy multi-sig). Palette UX uses one template each;
     * keep for hydration compatibility.
     */
    markLibrary: [],
    /** Independent field instances on the document. */
    placements: [],
    activePlacementId: null,
    /** When set, TEXT label dropdown auto-opens once for this placement id. */
    pendingTextLabelPickId: null,
  };
}

function newSignPdfLibraryId() {
  return `sp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function stampLibraryId(settings, libraryId) {
  return {
    ...settings,
    signPdfLibraryId: libraryId,
  };
}

/**
 * Seed library from existing placed marks (sessions created before multi-sig).
 */
export function hydrateSignPdfMarkLibrary(session) {
  if (!session) return createDefaultSignPdfSession();
  let library = Array.isArray(session.markLibrary)
    ? session.markLibrary.slice()
    : [];
  let next = session;
  let changed = false;

  const seedKind = (kind, shared, factory) => {
    if (!hasSignPdfSignature(shared)) return;
    const already = library.some(
      (entry) => entry?.kind === kind && hasSignPdfSignature(entry?.settings)
    );
    if (already) return;
    const id = shared.signPdfLibraryId || newSignPdfLibraryId();
    const settings = stampLibraryId(cloneMark(shared, factory), id);
    library = [...library, { id, kind, settings }];
    if (kind === SIGN_PDF_MARK_KINDS.INITIALS) {
      next = { ...next, initials: settings };
    } else {
      next = { ...next, signature: settings };
    }
    changed = true;
  };

  seedKind(
    SIGN_PDF_MARK_KINDS.SIGNATURE,
    session.signature,
    createDefaultSignPdfSettings
  );
  seedKind(
    SIGN_PDF_MARK_KINDS.INITIALS,
    session.initials,
    createDefaultSignPdfInitialsSettings
  );

  if (!changed && Array.isArray(session.markLibrary)) return session;
  return { ...next, markLibrary: library };
}

/** Saved library entries for a mark kind (newest last). */
export function listSignPdfLibrary(session, kind) {
  const hydrated = hydrateSignPdfMarkLibrary(session);
  const list = Array.isArray(hydrated.markLibrary) ? hydrated.markLibrary : [];
  return list.filter(
    (entry) =>
      entry?.kind === kind &&
      entry?.id &&
      hasSignPdfSignature(entry?.settings)
  );
}

export function getSignPdfLibraryEntry(session, libraryId) {
  if (!libraryId) return null;
  const hydrated = hydrateSignPdfMarkLibrary(session);
  return (
    (hydrated.markLibrary || []).find((entry) => entry?.id === libraryId) ||
    null
  );
}

/**
 * Save mark into the library and place it in the active slot.
 * Pass `libraryId` to update an existing entry; omit / `asNew` to add another.
 */
export function saveAndPlaceSignPdfMark(
  session,
  kind,
  markSettings,
  pageNum = 1,
  { libraryId = null, asNew = false } = {}
) {
  const activeKind =
    kind === SIGN_PDF_MARK_KINDS.INITIALS
      ? SIGN_PDF_MARK_KINDS.INITIALS
      : SIGN_PDF_MARK_KINDS.SIGNATURE;
  const factory =
    activeKind === SIGN_PDF_MARK_KINDS.INITIALS
      ? createDefaultSignPdfInitialsSettings
      : createDefaultSignPdfSettings;

  let next = hydrateSignPdfMarkLibrary(session);
  const library = Array.isArray(next.markLibrary)
    ? next.markLibrary.slice()
    : [];

  const existingId =
    !asNew &&
    (libraryId ||
      markSettings?.signPdfLibraryId ||
      getSignPdfPageMarks(next, pageNum)[
        activeKind === SIGN_PDF_MARK_KINDS.INITIALS ? "initials" : "signature"
      ]?.signPdfLibraryId);

  const id =
    existingId && library.some((entry) => entry.id === existingId)
      ? existingId
      : newSignPdfLibraryId();

  const stamped = stampLibraryId(cloneMark(markSettings, factory), id);
  const entry = { id, kind: activeKind, settings: stamped };
  const idx = library.findIndex((item) => item.id === id);
  if (idx >= 0) library[idx] = entry;
  else library.push(entry);

  next = { ...next, markLibrary: library };
  return setSignPdfMark(next, activeKind, stamped, pageNum);
}

/** Place a saved library entry into the signature/initials slot. */
export function placeSignPdfLibraryEntry(session, libraryId, pageNum = 1) {
  const entry = getSignPdfLibraryEntry(session, libraryId);
  if (!entry || !hasSignPdfSignature(entry.settings)) return session;
  const next = hydrateSignPdfMarkLibrary(session);
  return setSignPdfMark(
    next,
    entry.kind,
    stampLibraryId(entry.settings, entry.id),
    pageNum
  );
}

/**
 * Remove a saved library entry. Clears the placed mark if it used that entry.
 */
export function removeSignPdfLibraryEntry(session, libraryId, pageNum = 1) {
  if (!libraryId) return session;
  let next = hydrateSignPdfMarkLibrary(session);
  const library = (next.markLibrary || []).filter(
    (entry) => entry?.id !== libraryId
  );
  next = { ...next, markLibrary: library };

  const pageMarks = getSignPdfPageMarks(next, pageNum);
  if (pageMarks.signature?.signPdfLibraryId === libraryId) {
    next = clearSignPdfMark(next, SIGN_PDF_MARK_KINDS.SIGNATURE, pageNum);
  }
  if (pageMarks.initials?.signPdfLibraryId === libraryId) {
    next = clearSignPdfMark(next, SIGN_PDF_MARK_KINDS.INITIALS, pageNum);
  }

  // Also clear shared templates when not per-page (clearSignPdfMark already does).
  // If shared still points at removed id after per-page clear of another page:
  if (next.signature?.signPdfLibraryId === libraryId) {
    next = {
      ...next,
      signature: createDefaultSignPdfSettings(),
    };
  }
  if (next.initials?.signPdfLibraryId === libraryId) {
    next = {
      ...next,
      initials: createDefaultSignPdfInitialsSettings(),
    };
  }

  return next;
}

export const DEFAULT_SIGN_PDF_SESSION = createDefaultSignPdfSession();

export function isSignPdfMarkKind(kind) {
  return (
    kind === SIGN_PDF_MARK_KINDS.SIGNATURE ||
    kind === SIGN_PDF_MARK_KINDS.INITIALS
  );
}

function cloneMark(settings, fallbackFactory) {
  const raw = {
    ...(settings || fallbackFactory()),
    tile: "single",
    enabled: true,
  };
  // Keep canvas theme + ink color in sync (prevents white-on-white previews).
  if (raw.signPdfCanvasTheme) {
    return withCanvasTheme(raw, raw.signPdfCanvasTheme);
  }
  const color = String(raw.color || "").trim().toLowerCase();
  if (
    color === "#fff" ||
    color === "#ffffff" ||
    color === "white" ||
    color === "rgb(255, 255, 255)"
  ) {
    return withCanvasTheme(raw, "black_grey");
  }
  return withCanvasTheme(raw, DEFAULT_SIGN_PDF_CANVAS_THEME_ID);
}

function emptyPageEntry() {
  return {
    signature: createDefaultSignPdfSettings(),
    initials: createDefaultSignPdfInitialsSettings(),
  };
}

function prepareBakeMark(settings) {
  if (!hasSignPdfSignature(settings)) return null;
  return withSignPdfSingleMark({
    ...settings,
    enabled: true,
    textAlign:
      settings?.textAlign === "left" || settings?.textAlign === "right"
        ? settings.textAlign
        : "center",
    color: settings?.color || "#111111",
    opacity: Number(settings?.opacity) > 0 ? Number(settings.opacity) : 100,
  });
}

/** Shared (non-per-page) mark templates. */
export function getSignPdfMark(session, kind) {
  if (kind === SIGN_PDF_MARK_KINDS.INITIALS) {
    return session?.initials || createDefaultSignPdfInitialsSettings();
  }
  return session?.signature || createDefaultSignPdfSettings();
}

/**
 * Marks for a given page in the editor / bake.
 * ALL / CURRENT → shared templates.
 * PER_PAGE → byPage[page] (empty defaults if page not customized yet).
 */
export function getSignPdfPageMarks(session, pageNum = 1) {
  const scope = getSignPdfPageScope(session);
  if (scope !== SIGN_PDF_SCOPES.PER_PAGE) {
    return {
      signature: getSignPdfMark(session, SIGN_PDF_MARK_KINDS.SIGNATURE),
      initials: getSignPdfMark(session, SIGN_PDF_MARK_KINDS.INITIALS),
    };
  }
  const key = String(pageNum);
  const entry = session?.byPage?.[key];
  if (!entry) {
    return {
      signature: createDefaultSignPdfSettings(),
      initials: createDefaultSignPdfInitialsSettings(),
    };
  }
  // Re-normalize theme so reused page marks never lose canvas contrast.
  return {
    signature: cloneMark(
      entry.signature,
      createDefaultSignPdfSettings
    ),
    initials: cloneMark(
      entry.initials,
      createDefaultSignPdfInitialsSettings
    ),
  };
}

export function getActiveSignPdfMark(session, pageNum = 1) {
  const pageMarks = getSignPdfPageMarks(session, pageNum);
  const kind = session?.activeMark || SIGN_PDF_MARK_KINDS.SIGNATURE;
  return kind === SIGN_PDF_MARK_KINDS.INITIALS
    ? pageMarks.initials
    : pageMarks.signature;
}

export function getSignPdfGhostMark(session, pageNum = 1) {
  const pageMarks = getSignPdfPageMarks(session, pageNum);
  const active = session?.activeMark || SIGN_PDF_MARK_KINDS.SIGNATURE;
  const other =
    active === SIGN_PDF_MARK_KINDS.INITIALS
      ? pageMarks.signature
      : pageMarks.initials;
  return hasSignPdfSignature(other) ? other : null;
}

function writePageEntry(session, pageNum, patch) {
  const key = String(pageNum);
  const prev = session?.byPage?.[key] || emptyPageEntry();
  return {
    ...session,
    byPage: {
      ...(session?.byPage || {}),
      [key]: {
        signature: prev.signature || createDefaultSignPdfSettings(),
        initials: prev.initials || createDefaultSignPdfInitialsSettings(),
        ...patch,
      },
    },
  };
}

/**
 * Set a mark. In PER_PAGE scope, writes to byPage[pageNum] and mirrors into shared template.
 */
export function setSignPdfMark(session, kind, markSettings, pageNum = 1) {
  const nextMark = cloneMark(
    markSettings,
    kind === SIGN_PDF_MARK_KINDS.INITIALS
      ? createDefaultSignPdfInitialsSettings
      : createDefaultSignPdfSettings
  );
  const activeKind =
    kind === SIGN_PDF_MARK_KINDS.INITIALS
      ? SIGN_PDF_MARK_KINDS.INITIALS
      : SIGN_PDF_MARK_KINDS.SIGNATURE;

  let next = {
    ...session,
    activeMark: activeKind,
  };

  if (activeKind === SIGN_PDF_MARK_KINDS.INITIALS) {
    next = { ...next, initials: nextMark };
  } else {
    next = { ...next, signature: nextMark };
  }

  if (getSignPdfPageScope(next) === SIGN_PDF_SCOPES.PER_PAGE) {
    next = writePageEntry(next, pageNum, {
      [activeKind === SIGN_PDF_MARK_KINDS.INITIALS ? "initials" : "signature"]:
        nextMark,
    });
  }

  // Keep the matching library entry in sync when the placed mark is edited.
  const libraryId = nextMark?.signPdfLibraryId;
  if (libraryId && Array.isArray(next.markLibrary)) {
    next = {
      ...next,
      markLibrary: next.markLibrary.map((entry) =>
        entry?.id === libraryId
          ? { ...entry, kind: activeKind, settings: nextMark }
          : entry
      ),
    };
  }

  return next;
}

export function clearSignPdfMark(session, kind, pageNum = 1) {
  const empty =
    kind === SIGN_PDF_MARK_KINDS.INITIALS
      ? createDefaultSignPdfInitialsSettings()
      : createDefaultSignPdfSettings();

  if (getSignPdfPageScope(session) === SIGN_PDF_SCOPES.PER_PAGE) {
    const pageMarks = getSignPdfPageMarks(session, pageNum);
    const cleared =
      kind === SIGN_PDF_MARK_KINDS.INITIALS
        ? { ...pageMarks, initials: empty }
        : { ...pageMarks, signature: empty };
    let next = writePageEntry(session, pageNum, cleared);
    // Keep shared template in sync when clearing the mark being edited.
    if (kind === SIGN_PDF_MARK_KINDS.INITIALS) {
      next = { ...next, initials: empty };
    } else {
      next = { ...next, signature: empty };
    }
    const stillSig = hasSignPdfSignature(cleared.signature);
    const stillInit = hasSignPdfSignature(cleared.initials);
    next = {
      ...next,
      activeMark: stillSig
        ? SIGN_PDF_MARK_KINDS.SIGNATURE
        : stillInit
          ? SIGN_PDF_MARK_KINDS.INITIALS
          : SIGN_PDF_MARK_KINDS.SIGNATURE,
    };
    return next;
  }

  if (kind === SIGN_PDF_MARK_KINDS.INITIALS) {
    return {
      ...session,
      initials: empty,
      activeMark: hasSignPdfSignature(session?.signature)
        ? SIGN_PDF_MARK_KINDS.SIGNATURE
        : SIGN_PDF_MARK_KINDS.INITIALS,
    };
  }
  return {
    ...session,
    signature: empty,
    activeMark: hasSignPdfSignature(session?.initials)
      ? SIGN_PDF_MARK_KINDS.INITIALS
      : SIGN_PDF_MARK_KINDS.SIGNATURE,
  };
}

/** Remove all marks from one page (PER_PAGE only). */
export function clearSignPdfPage(session, pageNum = 1) {
  if (getSignPdfPageScope(session) !== SIGN_PDF_SCOPES.PER_PAGE) {
    return session;
  }
  const key = String(pageNum);
  const byPage = { ...(session?.byPage || {}) };
  delete byPage[key];
  return { ...session, byPage };
}

/** Shared library marks (last applied / template) — used to reuse across pages. */
export function getSignPdfLibraryMarks(session) {
  return {
    signature: getSignPdfMark(session, SIGN_PDF_MARK_KINDS.SIGNATURE),
    initials: getSignPdfMark(session, SIGN_PDF_MARK_KINDS.INITIALS),
  };
}

/**
 * Copy a library mark onto a page (PER_PAGE). No-op if library mark is empty.
 */
export function placeSignPdfLibraryMarkOnPage(session, kind, pageNum = 1) {
  if (getSignPdfPageScope(session) !== SIGN_PDF_SCOPES.PER_PAGE) {
    return session;
  }
  const library = getSignPdfLibraryMarks(session);
  const mark =
    kind === SIGN_PDF_MARK_KINDS.INITIALS
      ? library.initials
      : library.signature;
  if (!hasSignPdfSignature(mark)) return session;
  return setSignPdfMark(session, kind, mark, pageNum);
}

/**
 * Copy every non-empty library mark onto a page (PER_PAGE).
 */
export function placeSignPdfLibraryOnPage(session, pageNum = 1) {
  if (getSignPdfPageScope(session) !== SIGN_PDF_SCOPES.PER_PAGE) {
    return session;
  }
  const library = getSignPdfLibraryMarks(session);
  let next = session;
  if (hasSignPdfSignature(library.signature)) {
    next = setSignPdfMark(
      next,
      SIGN_PDF_MARK_KINDS.SIGNATURE,
      library.signature,
      pageNum
    );
  }
  if (hasSignPdfSignature(library.initials)) {
    next = setSignPdfMark(
      next,
      SIGN_PDF_MARK_KINDS.INITIALS,
      library.initials,
      pageNum
    );
  }
  return next;
}

export function setActiveSignPdfMark(session, kind) {
  if (!isSignPdfMarkKind(kind)) return session;
  return { ...session, activeMark: kind };
}

/**
 * Change page scope. Seeding:
 * - → PER_PAGE: copy shared marks onto current page if it has no entry yet
 * - → ALL/CURRENT from PER_PAGE: promote current page marks into shared templates
 */
export function setSignPdfPageScope(session, scope, pageNum = 1) {
  const nextScope = normalizeSignPdfPageScope(scope);
  const prevScope = getSignPdfPageScope(session);
  let next = { ...session, pageScope: nextScope };

  if (nextScope === SIGN_PDF_SCOPES.PER_PAGE && prevScope !== SIGN_PDF_SCOPES.PER_PAGE) {
    const key = String(pageNum);
    if (!next.byPage?.[key]) {
      next = writePageEntry(next, pageNum, {
        signature: cloneMark(
          session.signature,
          createDefaultSignPdfSettings
        ),
        initials: cloneMark(
          session.initials,
          createDefaultSignPdfInitialsSettings
        ),
      });
    }
  }

  if (
    prevScope === SIGN_PDF_SCOPES.PER_PAGE &&
    nextScope !== SIGN_PDF_SCOPES.PER_PAGE
  ) {
    const pageMarks = getSignPdfPageMarks(session, pageNum);
    next = {
      ...next,
      signature: cloneMark(
        pageMarks.signature,
        createDefaultSignPdfSettings
      ),
      initials: cloneMark(
        pageMarks.initials,
        createDefaultSignPdfInitialsSettings
      ),
    };
  }

  return next;
}

export function getSignPdfPageScope(session) {
  return normalizeSignPdfPageScope(session?.pageScope);
}

/** True when the user has something to place for this mark. */
export function hasSignPdfSignature(settings) {
  const mode = signPdfModeFromSettings(settings);
  if (mode === SIGN_PDF_MODES.UPLOAD || mode === SIGN_PDF_MODES.DRAW) {
    return Boolean(settings?.logoUrl);
  }
  return Boolean(String(settings?.text || "").trim());
}

export function canApplySignPdfDraft(settings) {
  return hasSignPdfSignature(settings);
}

/** Shared-template marks (ALL / CURRENT bake list). */
export function listSignPdfBakeMarks(session) {
  const marks = [];
  const sig = prepareBakeMark(session?.signature);
  const init = prepareBakeMark(session?.initials);
  if (sig) marks.push(sig);
  if (init) marks.push(init);
  return marks;
}

function listPageBakeMarks(pageMarks) {
  const marks = [];
  const sig = prepareBakeMark(pageMarks?.signature);
  const init = prepareBakeMark(pageMarks?.initials);
  if (sig) marks.push(sig);
  if (init) marks.push(init);
  return marks;
}

/**
 * Resolve which marks to stamp on a given PDF page (legacy signature+initials).
 * Prefer resolveSignPdfMarksForPage from signPdfPlacements.js when using placements.
 */
export function resolveSignPdfMarksForPage(
  session,
  pageNum,
  currentPage = 1
) {
  const scope = getSignPdfPageScope(session);

  if (scope === SIGN_PDF_SCOPES.PER_PAGE) {
    const key = String(pageNum);
    const entry = session?.byPage?.[key];
    if (!entry) return [];
    return listPageBakeMarks(entry);
  }

  const marks = listSignPdfBakeMarks(session);
  if (!marks.length) return [];
  if (scope === SIGN_PDF_SCOPES.CURRENT) {
    return Number(pageNum) === Number(currentPage) ? marks : [];
  }
  return marks;
}

/** Collect every mark that might be baked (for font loading). */
export function listAllSignPdfBakeMarks(session) {
  const scope = getSignPdfPageScope(session);
  if (scope !== SIGN_PDF_SCOPES.PER_PAGE) {
    return listSignPdfBakeMarks(session);
  }
  const out = [];
  const byPage = session?.byPage || {};
  for (const key of Object.keys(byPage)) {
    out.push(...listPageBakeMarks(byPage[key]));
  }
  return out;
}

export function signPdfScopeHint(session, currentPage = 1) {
  const scope = getSignPdfPageScope(session);
  if (scope === SIGN_PDF_SCOPES.CURRENT) {
    return `Download applies marks to page ${currentPage} only.`;
  }
  if (scope === SIGN_PDF_SCOPES.PER_PAGE) {
    return "Each page can have its own fields. Only customized pages are downloaded.";
  }
  return "Download applies the same signature and fields to all pages.";
}

export function hasAnySignPdfMark(session) {
  if (Array.isArray(session?.placements) && session.placements.length > 0) {
    return true;
  }
  const scope = getSignPdfPageScope(session);
  if (scope === SIGN_PDF_SCOPES.PER_PAGE) {
    return listAllSignPdfBakeMarks(session).length > 0;
  }
  return listSignPdfBakeMarks(session).length > 0;
}

export function pageHasSignPdfMarks(session, pageNum = 1) {
  return listPageBakeMarks(getSignPdfPageMarks(session, pageNum)).length > 0;
}
